import React from 'react'
import { isEmpty } from 'lodash'
import SportManager from '../managers/sport'
import SubscriptionManager from '../connection/subscription'
import { SportsbookError } from '../error'
import {
  removeFromArrayById,
  updateInArrayById,
  verifyCountersBySportServices,
  logger
} from '../utils'

import {
  CATEGORY_STATE_UPDATE,
  CATEGORY_TOURNAMENT_REMOVE,
  CATEGORY_TOURNAMENT_ADD,
  CATEGORY_TOURNAMENT_UPDATE,
  SPORT_CATEGORY_REMOVE
} from '../actions'

import {
  SPORT_SERVICE
} from '../constants'

import { useApplicationState } from './root'
import { useSportsbookState } from './sportsbook'
import { useSettings } from './settings'

export const CategoryContext = React.createContext()

export function CategoryProvider (props) {
  const {
    category,
    children
  } = props

  const [
    categoryState
  ] = createCategoryState(category)

  return (
    <CategoryContext.Provider value={categoryState}>
      {children}
    </CategoryContext.Provider>
  )
}

export const categoryInitialState = {
  tournaments: [],
  loading: true,
  initialized: false
}

export const useCategoryState = () => {
  return React.useContext(CategoryContext) || categoryInitialState
}

const categoryReducer = (state, action) => {
  switch (action.type) {
    case CATEGORY_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }
    case CATEGORY_TOURNAMENT_REMOVE:
      return {
        ...state,
        tournaments: removeFromArrayById(
          state.tournaments,
          action.payload.id
        )
      }
    case CATEGORY_TOURNAMENT_ADD:
      return {
        ...state,
        tournaments: updateInArrayById(
          state.tournaments,
          action.payload
        ).sort(
          (a, b) => (a.position - b.position)
        )
      }
    case CATEGORY_TOURNAMENT_UPDATE:
      return {
        ...state,
        tournaments: updateInArrayById(
          state.tournaments,
          action.payload
        )
      }
    default:
      return state
  }
}

const createCategoryInitialState = category => {
  return {
    ...category,
    ...categoryInitialState
  }
}

export const createCategoryState = (initialCategoryState) => {
  const [CategoryState, CategoryDispatcher] = React.useReducer(
    categoryReducer,
    initialCategoryState,
    createCategoryInitialState
  )

  const [sportsbookState] = useSportsbookState()
  const {
    sportService,
    sportServices,
    fromDate,
    toDate
  } = sportsbookState

  const { connection } = useApplicationState()
  const { lang } = useSettings()

  /*
    Category tournaments initial load
  */
  React.useEffect(
    () => {
      if (!CategoryState.id) { return }

      CategoryDispatcher({
        type: CATEGORY_STATE_UPDATE,
        payload: {
          loading: true,
          // initialized: false,
          error: null
        }
      })

      SportManager.findTournaments({
        filter: {
          sportService,
          sportServices,
          fromDate,
          toDate,
          sportId: CategoryState.sportId,
          categoryId: CategoryState.id
        }
      })
        .then(res => {
          if (isEmpty(res.data)) {
            throw new SportsbookError('No tournaments found', res)
          }

          return res.data.sort(
            (a, b) => (a.position - b.position)
          )
        })
        .then(tournaments => {
          CategoryDispatcher({
            type: CATEGORY_STATE_UPDATE,
            payload: {
              loading: false,
              initialized: true,
              tournaments
            }
          })
        })
        .catch(error => {
          logger.debug(
            `[category:${CategoryState.id}]`,
            'unable resolve category tournaments: ' + error
          )

          CategoryDispatcher({
            type: CATEGORY_STATE_UPDATE,
            payload: {
              loading: false,
              initialized: true,
              error
            }
          })
        })
    },
    [connection.id, CategoryState.id, lang, fromDate, toDate]
  )

  // Category self desturction
  React.useEffect(
    () => {
      if (CategoryState.initialized && isEmpty(CategoryState.tournaments)) {
        logger.debug(
          `[category:${CategoryState.id}]`,
          `self destructing initialized category in sport#${CategoryState.sportId} state`
        )

        //  Removing category from sport state
        SportManager.dataBroadcaster.emit(
          `sport:${CategoryState.sportId}`,
          {
            type: SPORT_CATEGORY_REMOVE,
            payload: {
              id: CategoryState.id
            }
          }
        )

        // Removing initialized flag from self category state
        CategoryDispatcher({
          type: CATEGORY_STATE_UPDATE,
          payload: {
            initialized: false
          }
        })
      }
    },
    [
      CategoryState.tournaments,
      CategoryState.initialized
    ]
  )

  /*
    Category dispatcher registration
    Category tournaments count subscriptions
  */
  React.useEffect(
    () => {
      if (!CategoryState.id) { return }
      // Subscribing on all tournaments couter updates
      // For this category
      const tournamentSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.TOURNAMENT_EVENT_COUNT,
          key: CategoryState.id
        }
      ]

      // Update handler
      const categoryUpdateHandler = action => (
        CategoryDispatcher(action)
      )

      SubscriptionManager.notifierSubscribe(tournamentSubscriptionTopics)

      // Register dispatcher for categories updates
      SportManager.dataBroadcaster.subscribeToCategoriesUpdates(
        CategoryState.id,
        categoryUpdateHandler
      )

      return () => {
        SubscriptionManager.notifierUnsubscribe(tournamentSubscriptionTopics)
        SportManager.dataBroadcaster.unsubscribeFromCategoriesUpdates(
          CategoryState.id,
          categoryUpdateHandler
        )
      }
    },
    [CategoryState.id]
  )

  /*
    Category tournaments counter subscription handling
  */
  React.useEffect(
    () => {
      if (!CategoryState.id) { return }

      // Handling tournaments counter updates in category scope
      const handleTournamentCounterUpdate = data => {
        const {
          id, // tournament id
          prematchCount,
          liveCount
        } = data

        // Defining by counter - should entity exist
        // or it must be removed
        const shouldExist = verifyCountersBySportServices(
          sportServices || sportService,
          {
            [SPORT_SERVICE.PREMATCH]: prematchCount,
            [SPORT_SERVICE.LIVE]: liveCount
          }
        )

        // For "should not exist" case
        if (!shouldExist) {
          // Dispatching tournament remove
          return CategoryDispatcher({
            type: CATEGORY_TOURNAMENT_REMOVE,
            payload: { id }
          })
        }

        const isTournamentExist = CategoryState.tournaments.find(
          tournament => tournament.id === id
        )

        // If tournament should exist and its present in category state
        if (isTournamentExist) {
          // Dispatching tournament update
          return CategoryDispatcher({
            type: CATEGORY_TOURNAMENT_UPDATE,
            payload: {
              id,
              prematchCount,
              liveCount
            }
          })
        }

        /*
         If tournament should exist but its not present in category state
         Resolving tournament
        */

        SportManager.findTournaments({
          filter: {
            sportService,
            sportServices,
            categoryId: CategoryState.id,
            sportId: CategoryState.sportId,
            tournamentIds: [
              id
            ]
          }
        })
          .then(res => {
            if (isEmpty(res.data)) { return }

            const [tournament] = res.data

            if (tournament) {
              // Dispatching tournament add
              CategoryDispatcher({
                type: CATEGORY_TOURNAMENT_ADD,
                payload: tournament
              })
            }
          })
      }

      SportManager.counterBroadcaster.subscribeToCategoriesUpdates(
        CategoryState.id,
        handleTournamentCounterUpdate
      )

      return () => {
        SportManager.counterBroadcaster.unsubscribeFromCategoriesUpdates(
          CategoryState.id,
          handleTournamentCounterUpdate
        )
      }
    },
    [
      CategoryState.id,
      CategoryState.tournaments
    ]
  )

  return [
    CategoryState,
    CategoryDispatcher
  ]
}
